import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { TFetchParams } from '@/types/fetch.type';
import { IPagination } from '@/types/general.type';
import { queryOptions, useQuery } from '@tanstack/react-query';

/**
 * Organization Channel type
 */
export type TOrganizationChannel = {
  organizationChannelId: string;
  displayName: string;
  createdAt: string; // ISO date string
  isActive: boolean;
  channel: {
    channelId: string;
    name: string;
  };
};

export type TGetOrganizationChannelResponse = ApiResponse<IPagination<TOrganizationChannel>>;
export type TGetOrganizationChannelParams = TFetchParams;

export const getOrganizationChannel = async (
  params: TGetOrganizationChannelParams
): Promise<TGetOrganizationChannelResponse> => {
  const url = '/integration/org-channel';

  return axiosInstance.get(url, { params });
};

/**
 * @description Query key for organization channel
 */
export const GET_ORGANIZATION_CHANNEL_QUERY_KEY = 'get-organization-channel';

export const getOrganizationChannelQueryOptions = <TData = TGetOrganizationChannelResponse>(
  params: TGetOrganizationChannelParams,
  select?: (data: TGetOrganizationChannelResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_ORGANIZATION_CHANNEL_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortBy: params?.sortBy,
        sortDir: params?.sortDir,
      },
    ],
    queryFn: () => getOrganizationChannel(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useOrganizationChannel<TData>(options: {
  params: TGetOrganizationChannelParams;
  queryConfig?: QueryConfig<typeof getOrganizationChannelQueryOptions<TData>>;
  select: (data: TGetOrganizationChannelResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useOrganizationChannel(options: {
  params: TGetOrganizationChannelParams;
  queryConfig?: QueryConfig<typeof getOrganizationChannelQueryOptions<TGetOrganizationChannelResponse>>;
}): ReturnType<typeof useQuery<TGetOrganizationChannelResponse>>;

// Implementation
export function useOrganizationChannel<TData = TGetOrganizationChannelResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetOrganizationChannelParams;
  queryConfig?: QueryConfig<typeof getOrganizationChannelQueryOptions<TData | TGetOrganizationChannelResponse>>;
  select?: (data: TGetOrganizationChannelResponse) => TData;
}) {
  return useQuery({
    ...getOrganizationChannelQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<TData extends TGetOrganizationChannelResponse ? TGetOrganizationChannelResponse : TData>
  >;
}
